import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { filter, distinctUntilChanged, take } from 'rxjs/operators';

import { Force } from '../forces/models';

// Convenient way to execute code on the last value emitted by an observable.
export const snapshot = <T>(observable: Observable<T>, callback: (val: T) => void) => {
    observable
        .pipe(
            filter((x) => x !== undefined),
            distinctUntilChanged(),
            take(1)
        )
        .subscribe((val) => {
            callback(val);
        });
};

export const dateString = (date: Date): string => {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    const hr = date.getHours().toString().padStart(2, '0');
    const min = date.getMinutes().toString().padStart(2, '0');
    const sec = date.getSeconds().toString().padStart(2, '0');
    const ms = date.getMilliseconds().toString().padStart(3, '0');
    return `${y}-${m}-${d} ${hr}:${min}:${sec}.${ms}`;
};

/**
 * Normalizes a force for comparison by stripping specified fields.
 * @param force The force to normalize
 * @param options.forceFieldsToIgnore Array of force field names to exclude from comparison (e.g., ['updatedAt', 'selected', 'cost'])
 * @param options.unitFieldsToIgnore Array of unit field names to exclude (e.g., ['unitTemplate'])
 */
const normalizeForceForComparison = (
    force: Force,
    options: { forceFieldsToIgnore?: string[]; unitFieldsToIgnore?: string[] } = {}
): any => {
    if (!force) return null;

    const { forceFieldsToIgnore = [], unitFieldsToIgnore = [] } = options;

    // Strip ignored fields from force
    const normalized: any = {};
    for (const key of Object.keys(force)) {
        if (!forceFieldsToIgnore.includes(key)) {
            normalized[key] = (force as any)[key];
        }
    }

    // Strip ignored fields from units if specified
    if (unitFieldsToIgnore.length > 0 && force.units) {
        normalized.units = force.units.map((u) => {
            const unitNormalized: any = {};
            for (const key of Object.keys(u)) {
                if (!unitFieldsToIgnore.includes(key)) {
                    unitNormalized[key] = (u as any)[key];
                }
            }
            return unitNormalized;
        });
    }

    return normalized;
};

/**
 * Compares two forces for meaningful equality, ignoring the updatedAt timestamp.
 * This prevents unnecessary re-renders when the only change is the sync timestamp.
 *
 * @param prev Previous force value
 * @param curr Current force value
 * @returns true if forces are meaningfully equal (only timestamp differs), false otherwise
 */
export const forceEqualsIgnoringTimestamp = (prev: Force, curr: Force): boolean => {
    if (!prev && !curr) return true;
    if (!prev || !curr) return false;
    if (prev === curr) return true;

    // Quick check - if IDs differ, forces are definitely different
    if (prev.id !== curr.id) return false;

    const prevNormalized = normalizeForceForComparison(prev, { forceFieldsToIgnore: ['updatedAt'] });
    const currNormalized = normalizeForceForComparison(curr, { forceFieldsToIgnore: ['updatedAt'] });

    return JSON.stringify(prevNormalized) === JSON.stringify(currNormalized);
};

/**
 * Compares two forces for cache validity, ignoring transient fields that don't affect computed state.
 * More lenient than forceEqualsIgnoringTimestamp - also ignores selected, cost, and unitTemplate.
 * @param prev Previous/cached force value
 * @param curr Current/incoming force value
 * @returns true if forces are equal for caching purposes
 */
export const forceEqualsForCache = (prev: Force, curr: Force): boolean => {
    if (!prev && !curr) return true;
    if (!prev || !curr) return false;
    if (prev === curr) return true;

    // Quick check - if IDs differ, forces are definitely different
    if (prev.id !== curr.id) return false;

    // Only compare user-controlled input fields. Computed output fields
    // (cost, derived, validationErrors, derivedStats on units) are excluded
    // because they are always recalculated by processForce and are never a
    // reliable signal that user inputs have changed. Including them causes
    // the cache check to always miss (store version has stale/absent
    // derivedStats; processed version has fresh derivedStats), which in turn
    // forces every force to be reprocessed on every pipeline emission.
    const prevNormalized = normalizeForceForComparison(prev, {
        forceFieldsToIgnore: ['updatedAt', 'selected', 'cost', 'derived', 'validationErrors', 'appVersion'],
        unitFieldsToIgnore: ['unitTemplate', 'derivedStats', 'valid', 'validationErrors']
    });
    const currNormalized = normalizeForceForComparison(curr, {
        forceFieldsToIgnore: ['updatedAt', 'selected', 'cost', 'derived', 'validationErrors', 'appVersion'],
        unitFieldsToIgnore: ['unitTemplate', 'derivedStats', 'valid', 'validationErrors']
    });

    return JSON.stringify(prevNormalized) === JSON.stringify(currNormalized);
};

export const retry = (intervals: number[], callback: (resolve, reject, i?: number) => void, runEveryIteration = false) => {
    let resolved = false;
    // intervals = [500];
    // const setTimeout = window['__zone_symbol__setTimeout'];
    return new Promise<any>((resolve, reject) => {
        intervals.forEach((i) => {
            console.debug(`~~~~ Retrying in ${i}ms`);
            setTimeout(() => {
                if (resolved && !runEveryIteration) {
                    return;
                }
                callback(
                    (value) => {
                        resolved = true;
                        resolve(value);
                    },
                    (reason: any) => {
                        if (i === intervals[intervals.length - 1]) {
                            const msg = `${reason} after ${i}ms`;
                            reject(msg);
                        }
                    },
                    i
                );
            }, i);
        });
    });
};

export const getUniqueId = () => Math.ceil(Math.random() * 100000);
export const sum = (agg, x) => agg + x;
export const hasValue = (x: any): boolean => x !== null && x !== undefined;

export const markFormTouched = (group: UntypedFormGroup | UntypedFormArray) => {
    Object.keys(group.controls).forEach((key: string) => {
        const control = group.controls[key];
        if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
            control.markAsTouched();
            markFormTouched(control);
        } else {
            control.markAsTouched();
        }
    });
};

export const validateEmail = (email: string) => {
    return String(email)
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
};

export const isFalsyNonZero = (val: any) => {
    return (val as any) === '' || val === null || val === undefined;
};

export const extractContentFromHTML = (html: string) => {
    const span = document.createElement('span');
    span.innerHTML = html;
    return span.textContent || span.innerText;
};

export interface DiffResult {
    equal: boolean;
    changedFields?: { [fieldKey: string]: { old: any; new: any } };
}

const isObjectBasicallyNull = (obj) => {
    if (obj === null || obj === undefined) {
        return true;
    }
    const keys = Object.keys(obj);
    for (const key of keys) {
        if (obj[key]) {
            if (typeof obj[key] === 'object') {
                return isObjectBasicallyNull(obj[key]);
            }
            return false;
        }
    }
    return true;
};

export const formDiff = (object1, object2): DiffResult => {
    const objKeys1 = Object.keys(object1);
    const objKeys2 = Object.keys(object2);
    const changedFields = {};
    const diffFields = [];

    if (isObjectBasicallyNull(object1) && isObjectBasicallyNull(object2)) {
        return {
            equal: true,
            changedFields: {}
        };
    }

    if (objKeys1.length !== objKeys2.length) {
        const k1 = objKeys1.filter((x) => !objKeys2.includes(x));
        const k2 = objKeys2.filter((x) => !objKeys1.includes(x));
        diffFields.push(...k1);
        diffFields.push(...k2);
    }

    for (const key of objKeys1) {
        const value1 = object1[key];
        const value2 = object2[key];

        const isObjects = isObject(value1) && isObject(value2);

        if (isObjects) {
            const diffResult = formDiff(value1, value2);
            if (!diffResult.equal) {
                for (const [childKey, val] of Object.entries(diffResult.changedFields)) {
                    changedFields[`${key}.${childKey}`] = val;
                }
            }
        } else if (!isObjects && value1 !== value2) {
            const equivalentNull = isObjectBasicallyNull(value1) && isObjectBasicallyNull(value2);
            if (!equivalentNull) {
                changedFields[key] = {
                    old: value1,
                    new: value2
                };
            }
        }
    }
    return {
        equal: Object.keys(changedFields).length === 0,
        changedFields
    };
};

const isObject = (object) => {
    return object != null && typeof object === 'object';
};

export const getAppVersionFromPackageVersion = (packageVersion: string): number[] => {
    return (packageVersion ?? '0.0.0').split('.').map((x) => parseInt(x?.replace(/v/gi, '')));
};

export const isVersionSupported = (version: string, targetVersion: string) => {
    const versionParts = getAppVersionFromPackageVersion(version);
    const targetVersionParts = getAppVersionFromPackageVersion(targetVersion);
    for (let i = 0; i < versionParts.length; i++) {
        if (versionParts[i] < targetVersionParts[i]) {
            return false;
        }
        if (versionParts[i] > targetVersionParts[i]) {
            return true;
        }
    }
    return true;
};

// Cache for performance optimization - avoids repeated array operations
let _unitIdCache: Set<string> | null = null;
let _lastForceUnits: any[] | null = null;

export const getUniqueUnitId = (force: Force) => {
    const generateId = () => Math.ceil(Math.random() * 100000).toString();

    if (!force) {
        return generateId();
    }

    // Performance optimization: Use cached Set for O(1) lookups instead of O(n) array.includes()
    // Only rebuild cache if force.units has changed (reference comparison)
    if (!_unitIdCache || _lastForceUnits !== force.units) {
        _unitIdCache = new Set(force.units.map((u) => u.id));
        _lastForceUnits = force.units;
    }

    let id: string;
    while (!id) {
        const tempId = generateId();
        if (!_unitIdCache.has(tempId)) {
            // O(1) Set lookup instead of O(n) array.includes()
            id = tempId;
        }
    }
    return id;
};

export function deepFreeze(o) {
    Object.freeze(o);
    if (o === undefined) {
        return o;
    }

    Object.getOwnPropertyNames(o).forEach(function (prop) {
        if (o[prop] !== null && (typeof o[prop] === 'object' || typeof o[prop] === 'function') && !Object.isFrozen(o[prop])) {
            deepFreeze(o[prop]);
        }
    });

    return o;
}

export function intersect<T>(arr1: T[], arr2: T[]) {
    if (!arr1 || !arr2) {
        return false;
    }
    return arr1.some((x) => arr2.includes(x));
}

export function isSubset<T>(arr1: T[], arr2: T[]) {
    if (!arr1 || !arr2) {
        return false;
    }
    return arr1.every((x) => arr2.includes(x));
}

export const deepMergeObjects = (base: Object, extension: Object) => {
    let merged = structuredClone(base);
    for (let key in extension) {
        if (typeof extension[key] === 'object') {
            if (typeof merged[key] !== 'object') {
                merged[key] = extension[key];
            } else {
                merged[key] = deepMergeObjects(merged[key], extension[key]);
            }
        } else {
            merged[key] = extension[key];
        }
    }

    return merged;
};
